import React, { useCallback, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { useQuery, useLazyQuery } from '@apollo/client';
import Chart from "chart.js/auto";
import { CategoryScale } from "chart.js";
import Container from '../components/common/Container';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { useTranslation } from 'react-i18next';
import Button from '../components/common/Button';
import DateSearchBox from '../components/common/DateSearchBox';
import PieChart from '../components/chart/PieChart';
import BarChart from '../components/chart/BarChart';
import LineChart from '../components/chart/LineChart';
import DownloadModal from '../components/modal/DownloadModal';
// import { Data } from "../constant/dashboard";
import { GET_VERIFICATION_DATA, GET_PERSONAL_VERIFICATION_DOWNLOADED } from "../graphql/queries";
import { EXPORT_PERSONAL_VERIFICATION_CSV, HAS_NOT_DOWNLOADED_DATA } from "../graphql/queries/personalVerification";
import { addOneDay } from '../utils/datetime';
import { getHourlyStats } from '../utils/statistics';
import Loading from '../components/common/Loading';
import { tokenPersistence } from '../utils/tokenPersistence';

Chart.register(CategoryScale);
dayjs.extend(quarterOfYear);

const ChartColors = {
    primary: 'rgba(54, 162, 235, 0.5)',
    success: 'rgba(75, 192, 192, 0.5)',
    danger: 'rgba(255, 99, 132, 0.5)',
    warning: 'rgba(255, 206, 86, 0.5)',
    borders: {
      primary: 'rgba(54, 162, 235, 1)',
      success: 'rgba(75, 192, 192, 1)',
      danger: 'rgba(255, 99, 132, 1)',
      warning: 'rgba(255, 206, 86, 1)'
    }
  };

enum PeriodOption {
    today = "today",
    yesterday = "yesterday",
    week = "week",
    month = "month",
    quarter = "quarter",
    year = "year"
}

enum TabList {
    identityVerification = "personal",
    corporateVerification = "legal person"
}

const today = dayjs();

const Dashboard: React.FC = () => {
    const auth = useSelector((state: RootState) => state.auth);
    const dashboard = useSelector((state: RootState) => state.dashboard);
    const { t, i18n } = useTranslation('dashboard');

    const [startDate, setStartDate] = useState<string>(today.startOf("day").format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState<string>(today.endOf("day").format("YYYY-MM-DD"));
    const [periodOption, setPeriodOption] = useState<PeriodOption>(PeriodOption.week);
    const [selectedTab, setSelectedTab] = useState<TabList>(TabList.identityVerification);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    const [dateChartData, setDateChartData] = useState({
        labels: [],
        datasets: [
            {
                label: t('total'),
                data: [],
                backgroundColor: [ChartColors.primary],
                borderColor: "white",
                borderWidth: 2
            },
            {
                label: t('approved'),
                data: [],
                backgroundColor: [ChartColors.success],
                borderColor: "white",
                borderWidth: 2
            },
            {
                label: t('denied'),
                data: [],
                backgroundColor: [ChartColors.danger],
                borderColor: "white",
                borderWidth: 2
            }
        ]
    });
    const [totalChartData, setTotalChartData] = useState({
        labels: [],
        datasets: [
            {
                label: t('approved'),
                data: [],
                backgroundColor: [ChartColors.primary],
                borderColor: "white",
                borderWidth: 2
            },
            {
                label: t('denied'),
                data: [],
                backgroundColor: [ChartColors.danger],
                borderColor: "white",
                borderWidth: 2
            },
            {
                label: t('in_progress'),
                data: [],
                backgroundColor: [ChartColors.warning],
                borderColor: "white",
                borderWidth: 2
            }
        ]
    });
    const [hourlyChartData, setHourlyChartData] = useState({
        labels: [],
        datasets: [
            {
                label: t('approved'),
                data: [],
                backgroundColor: [ChartColors.primary],
                borderColor: "green",
                borderWidth: 2
            },
            {
                label: t('denied'),
                data: [],
                backgroundColor: [ChartColors.success],
                borderColor: "blue",
                borderWidth: 2
            },
        ]
    })

    const { loading, error, data, refetch } = useQuery(GET_VERIFICATION_DATA, {
        variables: {
            method: selectedTab,
            startDate: today.startOf("day").format("YYYY-MM-DD"),
            endDate: today.endOf("day").format("YYYY-MM-DD"),
        }
    });

    const [csvLoading, setCsvLoading] = useState(false);
    const [exportCsvLazy] = useLazyQuery(EXPORT_PERSONAL_VERIFICATION_CSV);

    const exportCsv = async (downloadType: 'all' | 'downloadable' | 'redownload') => {
        setCsvLoading(true);
        try {
            const { data } = await exportCsvLazy({
                variables: {
                    startDate: null,
                    endDate: null,
                    downloadType: downloadType
                }
            });

            if (data?.exportPersonalVerificationCsv) {
                const csvContent = data.exportPersonalVerificationCsv;
                const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
                const blob = new Blob([BOM, csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.setAttribute('href', url);
                const timestamp = dayjs().format('YYYY-MM-DD_HH-mm-ss');
                link.setAttribute('download', `personal_verification_${downloadType}_${timestamp}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('CSV export error:', error);
            alert('Failed to export CSV. Please try again.');
        } finally {
            setCsvLoading(false);
        }
    };

    const { data: downloadData, refetch: refetchDownloadData } = useQuery(GET_PERSONAL_VERIFICATION_DOWNLOADED);

    const { data: hasNotDownloadedData } = useQuery(HAS_NOT_DOWNLOADED_DATA);

    useEffect(() => {
        let startDateTmp, endDateTmp

        if(periodOption === PeriodOption.today) {
            startDateTmp = today.startOf("day");
            endDateTmp = today.startOf("day");
        }

        if(periodOption === PeriodOption.yesterday) {
            startDateTmp = today.subtract(1, "day").startOf("day");
            endDateTmp = today.subtract(1, "day").startOf("day");
        }

        if(periodOption === PeriodOption.week) {
            startDateTmp = today.subtract(1, "week").startOf("day").add(1, "day");
            endDateTmp = today.endOf("day");
        }

        if(periodOption === PeriodOption.month) {
            startDateTmp = today.subtract(1, "month").startOf("day").add(1, "day");
            endDateTmp = today.endOf("day");
        }

        if(periodOption === PeriodOption.quarter) {
            startDateTmp = today.subtract(1, "quarter").startOf("day").add(1, "day");
            endDateTmp = today.endOf("day");
        }

        if(periodOption === PeriodOption.year) {
            startDateTmp = today.subtract(1, "year").startOf("day").add(1, "day");
            endDateTmp = today.endOf("day");
        }

        if(startDateTmp && endDateTmp) {
            setStartDate(startDateTmp.format("YYYY-MM-DD"));
            setEndDate(endDateTmp.format("YYYY-MM-DD"));
        }
    }, [periodOption])

    useEffect(() => {
        if(data) {
            const timeList = typeof data.verificationData.verificationItems === 'object' ? data.verificationData.verificationItems.map((item: any) => item.verificationDocumentSubmittedAt) : []

            setDateChartData({
                labels: data.verificationData.statistics.map((item: any) => item.date),
                datasets: [
                    {
                        label: t('total'),
                        data: data.verificationData.statistics.map((item: any) => item.totalNum || 0),
                        backgroundColor: [ChartColors.primary],
                        borderColor: "white",
                        borderWidth: 2
                    },
                    {
                        label: t('approved'),
                        data: data.verificationData.statistics.map((item: any) => item.approveNum || 0),
                        backgroundColor: [ChartColors.success],
                        borderColor: "white",
                        borderWidth: 2
                    },
                    {
                        label: t('denied'),
                        data: data.verificationData.statistics.map((item: any) => item.deniedNum || 0),
                        backgroundColor: [ChartColors.danger],
                        borderColor: "white",
                        borderWidth: 2
                    }
                ]
            });

            setTotalChartData({
                labels: [t('approved') as never, t('denied') as never, t('in_progress') as never],
                datasets: [
                    {
                        label: "",
                        data: [data.verificationData.approvedNum as never, data.verificationData.deniedNum as never, data.verificationData.inProgressNum as never],
                        backgroundColor: [ChartColors.success, ChartColors.danger, ChartColors.warning],
                        borderColor: "white",
                        borderWidth: 2
                    }
                ]
            });

            setHourlyChartData({
                labels: ['00:00', '01:00', '02:00', '03:00', '04:00', '05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00', '23:00'] as never[],
                datasets: [
                    {
                        label: t('trading volumn'),
                        data: getHourlyStats(timeList).time as never[],
                        backgroundColor: [ChartColors.primary],
                        borderColor: ChartColors.primary,
                        borderWidth: 2
                    },
                    {
                        label: t('cumulative'),
                        data: getHourlyStats(timeList).sum as never[],
                        backgroundColor: [ChartColors.success],
                        borderColor: ChartColors.success,
                        borderWidth: 2
                    },
                ]
            })
        }
    }, [data, t])

    useEffect(() => {
        refetch({
            method: selectedTab,
            startDate: startDate,
            endDate: endDate,
        });
    }, [selectedTab])

    useEffect(() => {
        refetch({
            method: selectedTab,
            startDate: startDate,
            endDate: endDate,
        });
    }, [startDate, endDate])

    const handleStartDate = useCallback((value: string) => {
        setStartDate(value);
    }, []);

    const handleEndDate = useCallback((value: string) => {
        setEndDate(value);
    }, []);

    const handlePeriodOption = useCallback((value: string) => {
        setPeriodOption(value as PeriodOption);
    }, []);

    const convertMinuteToTime = useCallback(() => {
        const minutes = data.verificationData.longestVerificationTimeMinutes;
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;

        return `${hours}${t('hours')}${remainingMinutes}${t('minutes')}`;
    }, [data, t]);

    const getSuccessRate = useCallback(() => {
        const totalNum = data.verificationData.totalNum;
        const approvedNum = data.verificationData.approvedNum;
        
        if (totalNum === 0) return "0%"; // Avoid division by zero
        
        const rate = (approvedNum / totalNum) * 100;

        return `${rate.toFixed(1)}%`;
    }, [data]);

    const handleCloseDownloadModal = useCallback(() => setIsDownloadModalOpen(false), []);

    const handleDownload = useCallback((type: 'all' | 'downloadable' | 'redownload') => {
        if (selectedTab === TabList.identityVerification) {
            exportCsv(type);
        }
        setIsDownloadModalOpen(false);
    }, [selectedTab, exportCsv]);
    
    if (loading) return <Loading />;
    if (error) return <p className="text-center mt-4 text-red-500">Error: {error.message}</p>;

    return (
        <Container label={t('dashboard')}>
            <div className="date-range-container">
                <label htmlFor="startDate">{t('period')}</label>
                <DateSearchBox label="" inputValue={startDate} placeholder={t('start_date')} hiddenLabel={true} onChange={handleStartDate} />
                <span>～</span>
                <DateSearchBox label="" inputValue={endDate} placeholder={t('end_date')} hiddenLabel={true} onChange={handleEndDate} />
                <div className="range-preset-buttons">
                    <Button label={t('today')} addedClass={periodOption === PeriodOption.today ? "active": ""} onClick={() => handlePeriodOption(PeriodOption.today)}/>
                    <Button label={t('yesterday')} addedClass={periodOption === PeriodOption.yesterday ? "active": ""} onClick={() => handlePeriodOption(PeriodOption.yesterday)}/>
                    <Button label={t('last_week')} addedClass={periodOption === PeriodOption.week ? "active": ""} onClick={() => handlePeriodOption(PeriodOption.week)}/>
                    <Button label={t('last_month')} addedClass={periodOption === PeriodOption.month ? "active": ""} onClick={() => handlePeriodOption(PeriodOption.month)}/>
                    <Button label={t('last_quarter')} addedClass={periodOption === PeriodOption.quarter ? "active": ""} onClick={() => handlePeriodOption(PeriodOption.quarter)}/>
                    <Button label={t('last_year')} addedClass={periodOption === PeriodOption.year ? "active": ""} onClick={() => handlePeriodOption(PeriodOption.year)}/>
                </div>
                <Button label={t('update')} onClick={() => {
                    refetch({
                        method: selectedTab,
                        startDate: startDate,
                        endDate: endDate,
                    });
                }} />
                {selectedTab === TabList.identityVerification && auth.role === 'admin' && (
                    <Button
                        label={csvLoading ? t('exporting') : t('export_csv')}
                        onClick={csvLoading ? () => {} : async () => {
                            await refetchDownloadData();
                            setIsDownloadModalOpen(true);
                        }}
                        addedClass={csvLoading ? 'opacity-50 cursor-not-allowed' : hasNotDownloadedData?.hasNotDownloadedData ? '': 'bg-yellow-600 hover:bg-yellow-700 text-white' }
                    />
                )}
            </div>
            <div className="tab-container">
                <div className="tab-buttons">
                    <Button
                        label={t('identity_verification')}
                        addedClass={`tab-button ${selectedTab === TabList.identityVerification ? "active" : ""}`}
                        onClick={() => {setSelectedTab(TabList.identityVerification)}}
                    />
                    {/*
                    <Button
                         label={t('corporate_verification')}
                        addedClass={`tab-button ${selectedTab === TabList.corporateVerification ? "active" : ""}`}
                        onClick={() => {setSelectedTab(TabList.corporateVerification)}}
                    />
                    */}
                </div>

                <div id="personal-tab" className="tab-content active">
                    <div className="metric-container">
                        <div className="metric-box">
                            <h4>{t('in_progress_transactions')}</h4>
                            <div className="metric-value" id="personal-current">{data.verificationData.inProgressNum.toLocaleString()}</div>
                        </div>
                        <div className="metric-box">
                            <h4>{t('longest_transaction_time')}</h4>
                            <div className="metric-value" id="personal-longest">{convertMinuteToTime()}</div>
                        </div>
                        <div className="metric-box">
                            <h4>{t('total_transactions_in_period')}</h4>
                            <div className="metric-value" id="personal-total">{data.verificationData.totalNum.toLocaleString()}</div>
                        </div>
                        <div className="metric-box">
                            <h4>{t('approval_rate')}</h4>
                            <div className="metric-value" id="personal-rate">{getSuccessRate()}</div>
                        </div>
                    </div>

                    <div className="dashboard-grid">
                        <div className="stats-card">
                            <h3>{t('daily_transactions')}</h3>
                            <div className="chart-container">
                                <BarChart chartData={dateChartData} />
                            </div>
                        </div>
                        <div className="stats-card">
                            <h3>{t('approval_denial_ratio')}</h3>
                            <div className="chart-container">
                                <PieChart chartData={totalChartData}/>
                            </div>
                        </div>
                        <div className="stats-card dashboard-full">
                            <h3>{t('hourly_transactions')}</h3>
                            <div className="chart-container">
                                <LineChart chartData={hourlyChartData}/>
                            </div>
                        </div>
                    </div>
                </div>
{/* 
                <div id="corporate-tab" className="tab-content">
                    <div className="metric-container">
                        <div className="metric-box">
                            <h4>{t('in_progress_transactions')}</h4>
                            <div className="metric-value" id="corporate-current">15</div>
                        </div>
                        <div className="metric-box">
                            <h4>{t('longest_transaction_time')}</h4>
                            <div className="metric-value" id="corporate-longest">2{t('hours')}45{t('minutes')}</div>
                        </div>
                        <div className="metric-box">
                            <h4>{t('total_transactions_in_period')}</h4>
                            <div className="metric-value" id="corporate-total">42</div>
                        </div>
                        <div className="metric-box">
                            <h4>{t('approval_rate')}</h4>
                            <div className="metric-value" id="corporate-rate">88.5%</div>
                        </div>
                    </div>

                    <div className="dashboard-grid">
                        <div className="stats-card">
                            <h3>{t('daily_transactions')}</h3>
                            <div className="chart-container">
                                <canvas id="corporate-daily-chart"></canvas>
                            </div>
                        </div>
                        <div className="stats-card">
                            <h3>{t('approval_denial_ratio')}</h3>
                            <div className="chart-container">
                                <canvas id="corporate-ratio-chart"></canvas>
                            </div>
                        </div>
                        <div className="stats-card dashboard-full">
                            <h3>{t('hourly_transactions')}</h3>
                            <div className="chart-container">
                                <canvas id="corporate-hourly-chart"></canvas>
                            </div>
                        </div>
                    </div>
                </div> */}
            </div>
            <DownloadModal
                isOpen={isDownloadModalOpen}
                onClose={handleCloseDownloadModal}
                downloadInfo={downloadData?.Downloaded}
                onDownload={handleDownload}
                hasNotDownloadedData={!hasNotDownloadedData?.hasNotDownloadedData}
            />
        </Container>
    );
};

export default Dashboard;
